import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { GitBranch, Clock, CheckCircle2, AlertCircle } from 'lucide-react';
import { api } from '../lib/api';

const STATUS_STYLES: Record<string, { color: string; icon: typeof Clock; label: string }> = {
  pending: { color: 'text-gray-400', icon: Clock, label: 'Pending' },
  analyzing: { color: 'text-blue-400', icon: AlertCircle, label: 'Analyzing' },
  ready_for_review: { color: 'text-amber-400', icon: AlertCircle, label: 'Ready for Review' },
  reviewed: { color: 'text-teal-400', icon: CheckCircle2, label: 'Reviewed' },
  completed: { color: 'text-emerald-400', icon: CheckCircle2, label: 'Completed' },
};

export default function CascadeViewPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['cascades'],
    queryFn: () => api.cascades.list(),
    staleTime: 30_000,
  });

  const cascades = data?.data ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">SOP Cascade Events</h1>
        <p className="text-sm text-muted-foreground">
          Policy changes that triggered downstream SOP updates
        </p>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      ) : cascades.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <GitBranch className="h-12 w-12 text-muted-foreground/30" />
          <p className="mt-4 text-lg font-medium">No cascade events yet</p>
          <p className="text-sm text-muted-foreground">
            Cascades are triggered automatically when policy reviews are completed
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {cascades.map((cascade: Record<string, unknown>) => {
            const status = STATUS_STYLES[cascade.status as string] ?? STATUS_STYLES.pending;
            const StatusIcon = status.icon;
            const targets = (cascade.affectedTargets ?? []) as Record<string, unknown>[];
            const changes = (cascade.approvedChanges ?? []) as Record<string, unknown>[];

            return (
              <Link
                key={cascade.id as string}
                to={`/cascades/${cascade.id as string}`}
                className="block rounded-lg border border-border bg-card p-5 transition-colors hover:border-teal-700"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <GitBranch className="h-5 w-5 text-teal-400" />
                    <div>
                      <p className="text-sm font-medium">
                        Policy cascade &mdash; {changes.length} approved change{changes.length !== 1 ? 's' : ''}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {targets.length} SOP{targets.length !== 1 ? 's' : ''} affected &middot;{' '}
                        Triggered {cascade.triggerType === 'policy_review_approved' ? 'by policy approval' : 'manually'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusIcon className={`h-4 w-4 ${status.color}`} />
                    <span className={`text-xs font-medium ${status.color}`}>{status.label}</span>
                  </div>
                </div>
                <div className="mt-3 text-xs text-muted-foreground">
                  Created {new Date(cascade.createdAt as string).toLocaleDateString('en-US', {
                    month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
                  })}
                  {Boolean(cascade.completedAt) && (
                    <> &middot; Completed {new Date(cascade.completedAt as string).toLocaleDateString('en-US', {
                      month: 'short', day: 'numeric',
                    })}</>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
